import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  Target, Loader2, Eye, MousePointerClick, Wallet, Filter,
} from "lucide-react";

/**
 * MA-CAMPAIGN-INCLUSION-LAYER-035: Campaign measurement management
 * Allows admins to include/exclude campaigns from MissionAssist measurement.
 */
export default function CampaignMeasurementManager() {
  const { toast } = useToast();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [filter, setFilter] = useState(null); // null = all

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter ? `?filter=${filter}` : "";
      const res = await api.get(`/campaign-inclusion/campaigns${q}`);
      setCampaigns(res.data.campaigns || []);
    } catch {
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const handleToggle = async (campaignId, include) => {
    setUpdating(campaignId);
    try {
      await api.put("/campaign-inclusion/update", {
        campaign_ids: [campaignId],
        included_in_measurement: include,
      });
      toast({
        title: include ? "Campaign tracked" : "Campaign excluded",
        description: include
          ? "This campaign will now appear in War Room analytics."
          : "This campaign has been removed from measurement.",
      });
      loadCampaigns();
    } catch {
      toast({ title: "Update failed", variant: "destructive" });
    } finally {
      setUpdating(null);
    }
  };

  const fmt = (n) => (n || 0).toLocaleString();
  const fmtMoney = (n) => `$${(n || 0).toFixed(2)}`;

  const filters = [
    { key: null, label: "All" },
    { key: "included", label: "Tracked" },
    { key: "excluded", label: "Excluded" },
    { key: "unreviewed", label: "Unreviewed" },
  ];

  return (
    <Card className="glass border-white/10" data-testid="campaign-measurement-manager">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-100">
            <Target className="h-5 w-5 text-indigo-400" />
            Campaign Measurement
          </CardTitle>
          <Badge variant="outline" className="text-[10px] border-slate-600 text-slate-400">
            {campaigns.filter((c) => c.included_in_measurement).length} tracked
          </Badge>
        </div>
        <p className="text-xs text-slate-400 mt-1">
          Only tracked campaigns appear in War Room analytics and count toward ROAS, CAC, and other metrics.
        </p>
      </CardHeader>
      <CardContent>
        {/* Filter tabs */}
        <div className="flex gap-1 mb-3 p-0.5 bg-white/[0.03] rounded-lg w-fit" data-testid="inclusion-filter-tabs">
          {filters.map((f) => (
            <button
              key={f.key || "all"}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                filter === f.key
                  ? "bg-white/10 text-slate-100"
                  : "text-slate-400 hover:text-slate-200"
              }`}
              data-testid={`inclusion-filter-${f.key || "all"}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="py-8 text-center">
            <Filter className="h-8 w-8 mx-auto mb-2 text-slate-500" />
            <p className="text-sm text-slate-400">
              {filter === "unreviewed"
                ? "All campaigns have been reviewed."
                : filter === "included"
                ? "No campaigns are currently tracked."
                : filter === "excluded"
                ? "No campaigns are excluded."
                : "No campaigns synced yet. Connect Meta and sync to see campaigns here."}
            </p>
          </div>
        ) : (
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1">
            {campaigns.map((c) => (
              <div
                key={c.campaign_id}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  c.included_in_measurement
                    ? "bg-indigo-500/[0.06] border-indigo-500/20"
                    : "bg-white/[0.02] border-white/[0.05]"
                }`}
                data-testid={`measurement-campaign-${c.campaign_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-100 truncate">{c.name}</p>
                    {c.source_platform && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-slate-600 text-slate-400 flex-shrink-0">
                        {c.source_platform}
                      </Badge>
                    )}
                    {!c.inclusion_reviewed && (
                      <Badge className="text-[10px] px-1.5 py-0 bg-amber-500/20 text-amber-300 border-amber-500/30 flex-shrink-0">
                        New
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <Wallet className="h-3 w-3" /> {fmtMoney(c.spend)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="h-3 w-3" /> {fmt(c.impressions)}
                    </span>
                    <span className="flex items-center gap-1">
                      <MousePointerClick className="h-3 w-3" /> {fmt(c.clicks)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-slate-400">
                    {c.included_in_measurement ? "Tracked" : "Not tracked"}
                  </span>
                  {updating === c.campaign_id ? (
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  ) : (
                    <Switch
                      checked={c.included_in_measurement}
                      onCheckedChange={(checked) => handleToggle(c.campaign_id, checked)}
                      data-testid={`measurement-toggle-${c.campaign_id}`}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
